import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';

/* Our locations — the places this company holds its events.

   Reached from the event editor, under the map field, because that is where you are standing
   when you notice the directions are wrong or the room is missing. The list arrives already
   filled: the first time anybody opens an editor, every venue the company's own events name is
   read out of them and written here, so a company that has been running events for two years
   sees its places rather than an empty screen.

   ★ Editing a place here does NOT touch the events already held there. Picking a location in
   the editor COPIES its three fields onto the event, and nothing links back — a past event is
   the record of where people actually went, and it must not move because somebody fixed a typo
   afterwards.

   ★ Which is also why deleting is a real delete, unlike retiring a category. A category is a
   link, so removing it would rewrite what past events WERE; a location is a copy, so removing
   it costs nothing but the entry in the picker. */
const LocationForm = ({ row, set, onSave, onCancel, busy, t }) => (
  <div className="bravos-form">
    <span className="bravos-form__label">{t('location')}</span>
    <input
      type="text"
      value={row.name}
      maxLength={50}
      placeholder={t('locationNamePlaceholder')}
      onChange={(e) => set({ ...row, name: e.target.value })}
    />
    <span className="bravos-form__label">{t('howToGetThere')}</span>
    <textarea
      rows={2}
      maxLength={2500}
      value={row.description || ''}
      onChange={(e) => set({ ...row, description: e.target.value })}
    />
    <span className="bravos-form__label">{t('mapEmbed')}</span>
    <textarea
      rows={2}
      maxLength={2500}
      value={row.embed || ''}
      placeholder="<iframe …>"
      onChange={(e) => set({ ...row, embed: e.target.value })}
    />
    <div className="bravos-form__row">
      <button
        type="button"
        className="bravos-submit"
        onClick={() => onSave(row)}
        disabled={busy || !row.name.trim()}
      >
        {busy ? t('pleaseWait') : t('save')}
      </button>
      <button type="button" className="bravos-submit bravos-submit--ghost" onClick={onCancel}>
        {t('cancel')}
      </button>
    </div>
  </div>
);

const EventLocations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [locations, setLocations] = useState(null);
  const [adding, setAdding] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/company/locations')
      .then((res) => setLocations(res.locations || []))
      // An employee, or a host who is not the admin, does not own the book. Back to the calendar
      // rather than onto an empty screen.
      .catch(() => navigate('/company-events'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const run = (promise) => {
    setBusy(true);
    setNotice(null);
    promise
      .then((res) => {
        setLocations(res.locations || []);
        setAdding(null);
        setEditing(null);
      })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const save = (row) => {
    if (!row.name.trim()) return;
    run(row.id
      ? apiRequest('PUT', `/company/locations/${row.id}`, row)
      : apiRequest('POST', '/company/locations', row));
  };

  const remove = (row) => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('deleteLocationConfirm', { name: row.name }))) return;
    run(apiRequest('DELETE', `/company/locations/${row.id}`));
  };

  const cancel = () => { setAdding(null); setEditing(null); };

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/company-events" />
        <Title my="1" className="mt-3">
          <Icon name="marker" theme="primary" className="me-2" />
          {t('ourLocations')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('ourLocationsNote')}</p>
      </Card>

      {locations === null ? <Card><span className="auth__spinner" /></Card> : (
        <Card>
          {locations.length === 0 && !adding && (
            <p className="ce-note">{t('noLocationsYet')}</p>
          )}

          {locations.map((row) => (
            editing && editing.id === row.id ? (
              <div key={row.id}>
                <LocationForm
                  row={editing}
                  set={setEditing}
                  onSave={save}
                  onCancel={cancel}
                  busy={busy}
                  t={t}
                />
              </div>
            ) : (
              <div key={row.id} className="ce-person">
                <span className="ce-person__body">
                  <span className="ce-person__name">{row.name}</span>
                  <span className="ce-person__email">
                    {row.used
                      ? t('locationUsed', { n: row.used, date: row.last_used })
                      : t('locationNeverUsed')}
                  </span>
                </span>
                <button
                  type="button"
                  className="ce-person__act"
                  onClick={() => { setEditing({ ...row }); setAdding(null); }}
                  disabled={busy}
                >
                  {t('edit')}
                </button>
                <button
                  type="button"
                  className="bravos-member__remove"
                  onClick={() => remove(row)}
                  disabled={busy}
                  title={t('delete')}
                >
                  <Icon name="trash" />
                </button>
              </div>
            )
          ))}

          {adding ? (
            <LocationForm
              row={adding}
              set={setAdding}
              onSave={save}
              onCancel={cancel}
              busy={busy}
              t={t}
            />
          ) : (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => { setAdding({ name: '', description: '', embed: '' }); setEditing(null); }}
            >
              <Icon name="plus" /> {t('newLocation')}
            </button>
          )}

          {notice && <p className="bravos-cal__notice">{notice}</p>}
        </Card>
      )}
    </Base>
  );
};

export default EventLocations;
